<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '📅 Isi Tanggal Kedaluwarsa Saat Terima Barang',
    icon: 'ri-calendar-check-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        Tanggal kedaluwarsa dicatat <strong>per batch penerimaan</strong>, diisi saat Anda
        mengonfirmasi <strong>Purchase Receiving</strong> (penerimaan barang dari
        supplier) — bukan di halaman Produk.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Pembelian → Supplier &amp; PO → Receiving</strong></li>
        <li class="mb-2">Klik <strong>"Buat Receiving"</strong>, pilih Purchase Order yang
          barangnya sudah datang</li>
        <li class="mb-2">Pada tabel item, isi kolom <strong>"Kedaluwarsa (opsional)"</strong>
          untuk bahan baku yang punya masa berlaku</li>
        <li class="mb-0">Lanjutkan konfirmasi receiving seperti biasa</li>
      </ol>
      <VAlert type="info" variant="tonal">
        💡 Kolom ini <strong>opsional</strong> — kosongkan saja untuk barang yang tidak
        punya tanggal kedaluwarsa (mis. peralatan, kemasan). Tidak mengisi kolom ini
        <strong>tidak mengganggu</strong> proses receiving/stok sama sekali.
      </VAlert>
    `,
  },
  {
    title: '📋 Laporan Stok Mendekati Kedaluwarsa',
    icon: 'ri-file-list-3-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Setelah ada batch yang tanggal kedaluwarsanya diisi, Anda bisa memantaunya lewat
        laporan <strong>Stok Kedaluwarsa</strong>.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Laporan → Stok Kedaluwarsa</strong></li>
        <li class="mb-2">Atur jumlah hari ke depan yang ingin dipantau (bawaan: 30 hari)</li>
        <li class="mb-0">Lihat daftar batch beserta status warnanya</li>
      </ol>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Status</th><th>Arti</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Aman</strong></td><td>Sisa hari masih lebih dari 14 hari</td></tr>
          <tr><td><strong>Segera</strong></td><td>Sisa 8–14 hari lagi</td></tr>
          <tr><td><strong>Mendesak</strong></td><td>Sisa 7 hari atau kurang</td></tr>
          <tr><td><strong>Sudah Lewat</strong></td><td>Tanggal kedaluwarsa sudah terlewati</td></tr>
        </tbody>
      </table>
    `,
  },
  {
    title: '⚠️ Catatan Jujur: Ini Sekadar Pengingat',
    icon: 'ri-error-warning-line',
    color: 'error',
    content: `
      <p class="mb-4">
        Penting untuk dipahami: fitur tracking kedaluwarsa di PintuKasir ini
        <strong>SEKADAR pengingat/pencatat</strong>, bukan sistem otomatis yang mengatur
        barang mana harus dijual/dipakai duluan.
      </p>
      <VAlert type="warning" variant="tonal" class="mb-4">
        <p class="mb-2"><strong>Yang TIDAK dilakukan sistem secara otomatis:</strong></p>
        <ul class="mb-0">
          <li>Sistem <strong>tidak</strong> mengurutkan atau memaksa barang dengan
            tanggal kedaluwarsa lebih dekat terjual/terpakai lebih dulu (bukan FEFO
            otomatis)</li>
          <li>Stok <strong>tetap dikurangi seperti biasa</strong> saat penjualan — sistem
            tidak "memilihkan" batch mana yang dipotong</li>
          <li>Laporan ini <strong>tidak mengubah</strong> alur stok/Purchase Receiving
            yang sudah berjalan sama sekali</li>
        </ul>
      </VAlert>
      <p class="mb-0">
        Dengan kata lain, laporan Stok Kedaluwarsa hanya membantu Anda <strong>melihat</strong>
        barang mana yang mendekati kedaluwarsa — keputusan barang mana yang dijual/dipakai
        lebih dulu tetap sepenuhnya di tangan pemilik/kasir secara manual.
      </p>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/retail">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Fitur Retail
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="error"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-calendar-close-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Tracking Kedaluwarsa
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Catat tanggal kedaluwarsa saat terima barang, pantau lewat laporan
        </p>
      </div>
    </div>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kalau saya tidak pernah mengisi tanggal kedaluwarsa, apa dampaknya?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak ada dampak apa pun ke alur stok/Purchase Receiving Anda. Laporan Stok
          Kedaluwarsa hanya akan tampil kosong — fitur ini murni opsional.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisakah saya edit tanggal kedaluwarsa setelah receiving dikonfirmasi?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tanggal kedaluwarsa dicatat sebagai bagian dari batch penerimaan saat receiving
          dikonfirmasi. Kalau ada kesalahan input, hubungi admin/pemilik toko untuk
          koreksi manual.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah sistem otomatis memilih batch mana yang dikurangi saat ada penjualan?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Fitur ini sengaja dirancang sederhana — sekadar pencatat & pengingat,
          bukan sistem FEFO (First-Expired-First-Out) otomatis. Urutan barang mana yang
          dijual/dipakai duluan tetap keputusan manual pemilik/kasir.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Toggle F&B & Retail', sub: 'Aktifkan dulu fitur Retail di Pengaturan', icon: 'ri-store-3-line', color: 'primary', to: { name: 'userguide-retail-toggle' } },
          { title: 'Receiving', sub: 'Panduan lengkap penerimaan barang dari supplier', icon: 'ri-download-cloud-2-line', color: 'warning', to: { name: 'userguide-pembelian-receiving' } },
          { title: 'Laporan Inventory', sub: 'Status stok dan valuasi', icon: 'ri-archive-stack-line', color: 'info', to: { name: 'userguide-laporan-inventory' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
